import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useHistory } from 'react-router'

import usePageLoaded from '../../hooks/usePageLoaded'

import Title from '../../components/ui/title/title'
import ProfilePreview from '../../components/profiles/profilePreview'
import PaginationLoader from '../../components/ui/paginationLoader/PaginationLoader'
import ProfilesFilter from '../../components/profiles/profilesFilter'
import PaginationAndEndlessScrollWrapper from '../index/PaginationAndEndlessScrollWrapper'
import { PaginationProfilesLink } from '../../components/ui/paginationLink/PaginationLink'

import { Formik } from 'formik'
import Pagination from 'rsuite/Pagination'
import { Helmet } from 'react-helmet'
import { META } from '../../constants/meta'

import { FetchProfiles } from '../../store/profiles/profilesTypings'
import { RootState } from '../../store/rootReducer'
import { Profile } from '../../store/commonTypes'
import {
    changeAmountOfPages,
    changeDefaultPageScrolledPagesAmountAndPosition,
    unfixAppContainer
} from '../../store/common/commonSlice'
import { ScrolledPageAmountAndPositionType } from '../../store/common/commonSliceTyping'

import { clearProfilesList, fetchProfiles } from '../../store/profiles/profilesSlice'
import { createLocalizedPath } from '../../utils/localizationHelpers'
import paths from '../../paths'
import { checkAuth } from '../../utils/auth'
import { getLangFromPath } from '../../i18next'
import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'
import '../index/pagination.sass'
import './profiles.sass'

export const initialProfilesValues: FetchProfiles = {
    p: 1,
    photo: 0,
    age: 0,
    query: '',
    gender: 0,
    region: '',
    country: '',
    certified: 0,
    contribution: 0,
    recently_online: 0
    // suggest: 0
}

let initialValuesProfilesClone = { ...initialProfilesValues }

const Profiles = () => {
    usePageLoaded()

    const [t] = useTranslation()
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()

    const list = useSelector<RootState, Profile[]>((state) => state.profiles.list)
    const loading = useSelector<RootState, boolean>((state) => state.profiles.loading)
    const total = useSelector<RootState, number>((state) => state.profiles.total)
    const scrolledPagesAmountAndPosition = useSelector<RootState, ScrolledPageAmountAndPositionType>(
        (state) => state.common.scrolledPagesAmountAndPosition
    )

    const [showMoreFilters, setShowMoreFilters] = useState(false)
    const lastElement = useRef(null)
    const AMOUNT_OF_PROFILES_ON_PAGE = 20

    const pageFromAdress = () => {
        if (location.search) return Number(new URLSearchParams(location.search).get('p'))
        return undefined
    }

    const [currentPage, setCurrentPage] = useState(
        isNumber(pageFromAdress()) ? pageFromAdress() : scrolledPagesAmountAndPosition.pages
    )

    return (
        <PaginationAndEndlessScrollWrapper
            clearList={clearProfilesList()}
            list={list}
            sourcePage={'profilesScrolling'}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            fetchList={fetchProfiles({
                ...initialValuesProfilesClone,
                // @ts-ignore
                p: currentPage
            })}
            lastElement={lastElement}
            loading={loading}
            total={+total}
        >
            <Formik
                initialValues={initialValuesProfilesClone || initialProfilesValues}
                onSubmit={(values: FetchProfiles): void => {
                    // @ts-ignore
                    initialValuesProfilesClone = { ...values }
                    setCurrentPage(1)
                    dispatch(changeAmountOfPages({ page: 1 }))
                    dispatch(clearProfilesList())
                    dispatch(fetchProfiles({ ...values }))
                    showMoreFilters && setShowMoreFilters(false)
                    dispatch(unfixAppContainer())

                    if (pageFromAdress())
                        history.push({
                            pathname: createLocalizedPath(paths.profiles.path),
                            search: `p=1`
                        })
                }}
            >
                <div className="pageContent" style={{ margin: '0 auto', maxWidth: '1360px', padding: '90px 27px' }}>
                    <Helmet>
                        <title>{META.profiles[getLangFromPath(location.pathname)].title}</title>
                        <meta
                            name="description"
                            content={META.profiles[getLangFromPath(location.pathname)].description}
                        />
                    </Helmet>
                    <Title text={t('Profiles_page')} />
                    <ProfilesFilter showMoreFilters={showMoreFilters} setShowMoreFilters={setShowMoreFilters} />
                    <div className="membresList">
                        {list.map((profile: Profile) =>
                            profile.nick ? <ProfilePreview profile={profile} key={profile.id} /> : <></>
                        )}
                    </div>
                    <div className="paginationWrapper">
                        {!loading ? (
                            <Pagination
                                ref={lastElement}
                                limit={AMOUNT_OF_PROFILES_ON_PAGE}
                                limitOptions={[10]}
                                maxButtons={5}
                                activePage={pageFromAdress() || scrolledPagesAmountAndPosition.pages}
                                linkAs={PaginationProfilesLink}
                                onChangePage={(page: number) => {
                                    history.push({
                                        pathname: createLocalizedPath(paths.profiles.path),
                                        search: `p=${page}`
                                    })
                                }}
                                total={+total}
                            />
                        ) : (
                            <PaginationLoader />
                        )}
                    </div>
                </div>
            </Formik>
        </PaginationAndEndlessScrollWrapper>
    )
}

// @ts-ignore
Profiles.getInitialProps = async ({ store, req }) => {
    await checkAuth(store)
    const {
        profiles: { list }
    } = store.getState()
    const scrolledPagesAmountAndPosition = store.getState().common.scrolledPagesAmountAndPosition
    if (isEmpty(list)) {
        if (req) {
            const page = req.query.p
            await store.dispatch(fetchProfiles({ ...initialProfilesValues, p: page || 1 }))
        } else {
            await store.dispatch(fetchProfiles(initialProfilesValues))
        }
    }
    if (scrolledPagesAmountAndPosition.sourcePage != 'profilesScrolling')
        store.dispatch(changeDefaultPageScrolledPagesAmountAndPosition())
}

export default Profiles
